import { Drawer, Portal } from '@chakra-ui/react';
import { useCallback, useMemo, useRef, useState } from 'react';

import Box from 'shared_DEPRECATED/components/Box';
import { useOnClickOutside } from 'shared_DEPRECATED/hooks';
import useEventsSubscription from 'shared_DEPRECATED/hooks/useEventsSubscription';
import { isElementHasParent } from 'shared_DEPRECATED/utils';

import { SidebarProvider } from 'shared/components/Sidebar/context';
import {
  SIDEBAR_CLOSE_EVENT,
  SIDEBAR_OPEN_EVENT,
  SIDEBAR_SET_CONTEXT_EVENT,
} from 'shared/components/Sidebar/eventPublishers';

const initialContent = null;
const initialProps = { title: '', size: 'xs' };
const initialContext = { isSidebarInited: false };

const SidebarContainer = () => {
  const [open, setOpen] = useState(false);
  const sidebarRef = useRef(null);
  const [content, setContent] = useState(initialContent);
  const [context, setContext] = useState(initialContext);
  const [sidebarProps, setSidebarProps] = useState(initialProps);

  const onSidebarOpen = useCallback(
    (event) => {
      const data = event.detail;
      const { content, props: newProps, context: newContext } = data;

      setContent(content);
      setSidebarProps({ ...initialProps, ...newProps });
      setContext({ ...initialContext, ...newContext });
      setOpen(true);
    },
    [setOpen]
  );

  const onSidebarClose = useCallback(
    (event) => {
      const isDialogParentOfTarget = isElementHasParent(event.target, 'DIALOG');

      if (!content || isDialogParentOfTarget) {
        return;
      }

      setContent(initialContent);
      setSidebarProps(initialProps);
      setContext(initialContext);
      setOpen(false);
    },
    [content, setOpen]
  );

  const onContextSet = useCallback(
    (event) => {
      const data = event.detail;

      setContext((prevContext) => ({ ...prevContext, ...data }));
    },
    [setContext]
  );

  const onSidebarInit = useCallback(() => {
    setContext((prevContext) => ({ ...prevContext, isSidebarInited: true }));
  }, [setContext]);

  const events = useMemo(
    () => [
      [SIDEBAR_OPEN_EVENT, onSidebarOpen],
      [SIDEBAR_CLOSE_EVENT, onSidebarClose],
      [SIDEBAR_SET_CONTEXT_EVENT, onContextSet],
    ],
    [onSidebarOpen, onSidebarClose, onContextSet]
  );

  useEventsSubscription(events, onSidebarInit);

  useOnClickOutside(sidebarRef, () => setOpen(false));

  return (
    <Box title={sidebarProps.title}>
      <Drawer.Root
        open={open}
        onOpenChange={(e) => setOpen(e.open)}
        size={sidebarProps.size}
      >
        <SidebarProvider {...context}>
          <Portal>
            <Drawer.Backdrop />
            <Drawer.Positioner>
              <Drawer.Content>{content}</Drawer.Content>
            </Drawer.Positioner>
          </Portal>
        </SidebarProvider>
      </Drawer.Root>
    </Box>
  );
};

export default SidebarContainer;
